import React, { ReactElement, useCallback } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  Box,
  Link,
  Tabs,
  TabItem,
  TabList,
  TabPanel,
} from '@razorpay/blade/components';
import OnboardingStatus, { OnboardingItem } from 'components/ui/OnboardingStatus';
import MainCase from 'components/ui/MainCase';
import PageWrapper from 'components/ui/PageWrapper';
import { routeToStepMap, stepToRouteMap, complianceSteps } from './utils';
import TdsSetup from './TdsSetup';
import { routePaths } from 'components/Routes/data';
import PtSetup from './PtSetup';
import PfSetup from './PfSetup';
import EsicSetup from './EsicSetup';
import GratuityLeaveEncashmentSetup from './GratuityLeaveEncashmentSetup';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { trackSegment } from 'utils/segment';

const STEP_COMPLIANCE = 1;
const STEP_PEOPLE = 2;

const Compliance = () => {
  const location = useLocation();
  const history = useHistory();
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);
  const { isBaseCompensationFloorEnabled } = useIsFeatureDisabledForRearch();

  const currentStep = routeToStepMap[location.pathname];
  const handleTabChange = useCallback((value: string) => {
    history.push(stepToRouteMap[value]);
  }, []);

  const handleStepChange = (value: number) => {
    if (value === STEP_PEOPLE) {
      window.location.href = routePaths.people;
    }
  };

  if (!(location.pathname in routeToStepMap)) {
    return <Redirect to={routePaths.unknown} />;
  }

  if (
    location.pathname === routePaths.companyOnboarding.compliance.gratuityLe &&
    !isBaseCompensationFloorEnabled
  ) {
    return <Redirect to={routePaths.companyOnboarding.compliance.root} />;
  }

  return (
    <Box width="100%">
      <OnboardingStatus
        className="mb-16 mx-auto"
        value={STEP_COMPLIANCE}
        title="Setup compliance"
        onChange={handleStepChange}>
        <OnboardingItem
          title="Setup compliance"
          value={STEP_COMPLIANCE}
          status={
            currentOrganization?.onboardingConfig?.isTDSSetupComplete &&
            currentOrganization.onboardingConfig.isPTSetupComplete &&
            currentOrganization.onboardingConfig.isPFSetupComplete &&
            currentOrganization.onboardingConfig.isESICSetupComplete
              ? 'completed'
              : undefined
          }
        />
        <OnboardingItem
          title="Add employees and contractors"
          value={STEP_PEOPLE}
          status={(currentOrganization?.noOfEmployees || 0) >= 2 ? 'completed' : undefined}
        />
      </OnboardingStatus>
      <Tabs variant="bordered" value={currentStep} onChange={handleTabChange}>
        <TabList>
          <TabItem value={complianceSteps.STEP_TDS}>TDS</TabItem>
          <TabItem value={complianceSteps.STEP_PT}>Professional Tax</TabItem>
          <TabItem value={complianceSteps.STEP_PF}>PF</TabItem>
          <TabItem value={complianceSteps.STEP_ESIC}>ESIC</TabItem>
          {isBaseCompensationFloorEnabled && (
            <TabItem value={complianceSteps.STEP_GRATUITY_LE}>Gratuity &amp; LE</TabItem>
          )}
        </TabList>
        <TabPanel value={complianceSteps.STEP_TDS}>
          <Box paddingTop="spacing.10">
            <TdsSetup />
          </Box>
        </TabPanel>
        <TabPanel value={complianceSteps.STEP_PT}>
          <Box paddingTop="spacing.10">
            <PtSetup />
          </Box>
        </TabPanel>
        <TabPanel value={complianceSteps.STEP_PF}>
          <Box paddingTop="spacing.10">
            <PfSetup />
          </Box>
        </TabPanel>
        <TabPanel value={complianceSteps.STEP_ESIC}>
          <Box paddingTop="spacing.10">
            <EsicSetup />
          </Box>
        </TabPanel>
        {isBaseCompensationFloorEnabled && (
          <TabPanel value={complianceSteps.STEP_GRATUITY_LE}>
            <Box paddingTop="spacing.10">
              <GratuityLeaveEncashmentSetup />
            </Box>
          </TabPanel>
        )}
      </Tabs>
      {currentStep !== complianceSteps.STEP_TDS &&
        currentStep !== complianceSteps.STEP_PT &&
        currentStep !== complianceSteps.STEP_GRATUITY_LE && (
          <Box maxWidth="1024px" marginX="auto" marginTop="spacing.10" marginBottom="spacing.10">
            <Link
              variant="button"
              color="neutral"
              icon={ArrowLeftIcon}
              iconPosition="left"
              onClick={() => {
                trackSegment('onboarding.setup_compliance.back_to_setup_guide_click', {
                  queue: true,
                });
                window.location.href = '/';
              }}>
              Back to setup guide
            </Link>
          </Box>
        )}
    </Box>
  );
};

function ComplianceWithPageWrapper(): ReactElement {
  return (
    <PageWrapper>
      <MainCase type="fullWidth">
        <Compliance />
      </MainCase>
    </PageWrapper>
  );
}

export default ComplianceWithPageWrapper;
